import React, { ChangeEvent, memo, useCallback, useEffect, useState } from "react"

import { StrictOmit, StringKeyOf } from "../../Types/helperTypes"
import { makeStyles, TextField, TextFieldProps, Typography } from "@material-ui/core"
import { FormikValues, useField, useFormikContext } from "formik"
import AsteriskComponent from "../Layout/Asterisk"
import { ValidationRule, ValidationType } from "../../Modules/Forms/formTypes"

const useStyles = makeStyles((theme) => ({
    textInput: {
        minHeight: "4rem",
    },
    mb_1: {
        marginBottom: theme.spacing(1),
    }
}))

export type FormikTextFieldProps<Values extends FormikValues> = Readonly<
    StrictOmit<TextFieldProps, "name" | "value" | "error" | "onBlur" | "onChange"> & {
    name: StringKeyOf<Values> // make name required and constrain it to the values managed by Formik
    validations: ValidationRule[]
    title?: string
    dimensionId?: number
    required?: boolean
}
    >

const AccountingDimensionTextField = <Values extends FormikValues>({ name, label, validations, placeholder, dimensionId, helperText, required, ...rest }: FormikTextFieldProps<Values>) => {
    const classes = useStyles()
    const [field, meta] = useField(name)
    const { setFieldValue } = useFormikContext();
    const [value, setValue] = useState('')

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setValue(e.target.value)
    }, [])

    useEffect(() => {
        setFieldValue(field.name, dimensionId && value ? [{ id: dimensionId, value: value || '' }] : [])
    }, [field.name, dimensionId, value])

    return (
        <>
            {placeholder && label && (
                <Typography variant="body1" className={classes.mb_1}>
                    {label}
                    <AsteriskComponent required={required || false} />
                </Typography>
            )}
            <TextField
                name={field.name}
                value={value}
                label={!placeholder && label ? label : ''}
                placeholder={placeholder}
                error={meta.touched && Boolean(meta.error)}
                helperText={(meta.touched && meta.error) || helperText}
                onBlur={field.onBlur}
                onChange={handleChange}
                className={classes.textInput}
                {...rest}
            />
        </>
    )
}

export default memo(AccountingDimensionTextField)
